import React, { useEffect, useState } from 'react';
import * as S from '../../../../../components/styles/modal-style';
import { Overlay, ErrorText, InfoText } from './QuickModal.style';
import PriceManager, { PriceItem } from '../../../../../components/PriceManager';
import ApiService from '../../../../../services/ApiService';
import OrdenoLoading from '../../../../../components/OrdenoLoading';

interface QuickStockPriceModalProps {
  isOpen: boolean;
  onClose: () => void;
  stockId: number;
  stockName: string;
  /** Unidade soberana do item — a que está sem custo no cadastro. */
  missingUnitName?: string;
  onSaved: (novos: PriceItem[]) => void;
  /** 'cost' (padrão) edita os custos por unidade; 'markup' edita o markup do estoque. */
  mode?: 'cost' | 'markup';
  onMarkupSaved?: (markup: number) => void;
}

/**
 * Custos do estoque a partir da venda: mostra os já cadastrados (em leitura) e permite
 * adicionar novos por unidade — mesmo PriceManager da tela de estoque. Persiste cada
 * custo novo via upsert; os existentes só são alterados na tela de estoque.
 */
const QuickStockPriceModal: React.FC<QuickStockPriceModalProps> = ({
  isOpen, onClose, stockId, stockName, missingUnitName, onSaved,
  mode = 'cost', onMarkupSaved,
}) => {
  const isMarkup = mode === 'markup';
  const [markupInput, setMarkupInput] = useState('');
  const [pricesList, setPricesList] = useState<PriceItem[]>([]);
  const [lockedUnitIds, setLockedUnitIds] = useState<number[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  // Busca os preços frescos do cadastro ao abrir — o objeto do pai pode estar mínimo
  // (hidratação de pedido salvo traz só {id, name}) ou defasado.
  useEffect(() => {
    if (!isOpen) return;
    let active = true;
    setLoading(true);
    setError('');
    ApiService.steelOrders.getStocks({ id: stockId })
      .then((res: any) => {
        if (!active) return;
        const existentes = (res.data?.stocks?.[0]?.prices || []).map((p: any) => ({
          price: Number(p.price),
          unitOfMeasurementId: Number(p.unitOfMeasurementId),
        }));
        setPricesList(existentes);
        setLockedUnitIds(existentes.map((p: PriceItem) => p.unitOfMeasurementId));
        const atual = res.data?.stocks?.[0]?.markup;
        setMarkupInput(atual !== undefined && atual !== null ? String(atual) : '');
      })
      .catch(() => {
        if (!active) return;
        setPricesList([]);
        setLockedUnitIds([]);
      })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [isOpen, stockId]);

  if (!isOpen) return null;

  const novos = pricesList.filter(p => !lockedUnitIds.includes(Number(p.unitOfMeasurementId)));

  const handleConfirmMarkup = async () => {
    setError('');
    const valor = Number(String(markupInput).replace(',', '.'));
    if (!markupInput || isNaN(valor) || valor <= 0) {
      setError('Informe um markup maior que zero. Ex.: 1.5');
      return;
    }
    setSaving(true);
    try {
      await ApiService.stock.update(stockId, { markup: valor });
      onMarkupSaved?.(valor);
      onClose();
    } catch (e: any) {
      const message = e?.response?.data?.message;
      setError(Array.isArray(message) ? message.join(' ') : (message || 'Erro ao salvar. Tente novamente.'));
    } finally {
      setSaving(false);
    }
  };

  const handleConfirm = async () => {
    setError('');
    if (novos.length === 0) {
      setError('Adicione ao menos um custo novo.');
      return;
    }
    setSaving(true);
    try {
      for (const p of novos) {
        await ApiService.stock.addStockPrice(stockId, {
          price: Number(p.price),
          unitOfMeasurementId: Number(p.unitOfMeasurementId),
        });
      }
      onSaved(novos);
      onClose();
    } catch (e: any) {
      const message = e?.response?.data?.message;
      setError(Array.isArray(message) ? message.join(' ') : (message || 'Erro ao salvar. Tente novamente.'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Overlay onClick={onClose}>
      <S.ContainerOfModal width="560px" onClick={(e: React.MouseEvent) => e.stopPropagation()}>
        <S.Header>
          <S.HeaderTitle>{isMarkup ? 'Markup do estoque' : 'Custos do estoque'}</S.HeaderTitle>
        </S.Header>
        <InfoText>
          <strong>{stockName}</strong>
          {isMarkup ? (
            <> está sem markup no cadastro. O valor vale para as próximas vendas deste estoque.</>
          ) : (
            <>
              {missingUnitName ? <> está sem custo em <strong>{missingUnitName}</strong>.</> : '.'}
              {' '}Os custos abaixo já estão no cadastro; adicione os que faltam — valem para as próximas vendas.
            </>
          )}
        </InfoText>
        {loading ? (
          <OrdenoLoading variant='inline' size='sm' text='Carregando dados do cadastro...' style={{ padding: '1rem 0' }} />
        ) : isMarkup ? (
          <S.InputDiv>
            <S.Label htmlFor="quick-stock-markup">Markup</S.Label>
            <S.Input
              id="quick-stock-markup"
              autoComplete="off"
              type="number"
              step="0.01"
              min="0"
              value={markupInput}
              onChange={(e) => setMarkupInput(e.target.value)}
              placeholder="Ex: 1.5"
              style={{ height: '40px' }}
            />
          </S.InputDiv>
        ) : (
          <PriceManager
            prices={pricesList}
            onChange={setPricesList}
            baseUrl={`${process.env.REACT_APP_GATEWAY_BASE_URL}/resources`}
            label="Custos"
            lockedUnitIds={lockedUnitIds}
          />
        )}
        {error && <ErrorText>{error}</ErrorText>}
        <S.BottomDiv>
          <S.ButtonContainer>
            <S.Button type="button" onClick={onClose} disabled={saving}>Cancelar</S.Button>
            {isMarkup ? (
              <S.Button type="button" onClick={handleConfirmMarkup} disabled={saving || !markupInput}>
                {saving ? 'Salvando…' : 'Salvar markup'}
              </S.Button>
            ) : (
              <S.Button type="button" onClick={handleConfirm} disabled={saving || novos.length === 0}>
                {saving ? 'Salvando…' : novos.length > 1 ? `Salvar ${novos.length} custos` : 'Salvar custo'}
              </S.Button>
            )}
          </S.ButtonContainer>
        </S.BottomDiv>
      </S.ContainerOfModal>
    </Overlay>
  );
};

export default QuickStockPriceModal;
